import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { environment } from '../../../environments/environment';
import { Observable, catchError, map, take, throwError } from "rxjs";
import { LoggerService } from "./utils/logger.service";

@Injectable({
  providedIn: 'any'
})
export class VideoRecordService {

  constructor(
    private _http: HttpClient,
    private loggerService: LoggerService
  ) { }

  /**
   * @description Restituisce la lista delle registrazioni collegate a quella notifica
   */
  public getNotificationAlertVideos$(notificationId: string): Observable<INotificationRecording[]> {
    return this._http.get<unknown>(`${environment.apiEndpoint}/notifications/${notificationId}/recordings`).pipe(
      take(1),
      map((response) => this.normalizeRecordings(response)),
      catchError(err => {
        this.loggerService.error(`Error while loading recordings for notification ${notificationId}`);
        return throwError(() => err);
      })
    );
  }

  /**
   * @description Restituisce il primo recording disponibile per la notifica
   */
  public getPrimaryNotificationAlertVideo$(notificationId: string): Observable<INotificationRecording | null> {
    return this.getNotificationAlertVideos$(notificationId).pipe(
      map((recordings) => recordings[0] || null)
    );
  }

  /**
   * @description Richiede la preparazione del video
   */
  public demandVideo$(notificationId: string, recordingId: string): Observable<unknown> {
    return this._http.post<unknown>(`${environment.apiEndpoint}/notifications/${notificationId}/recordings/${recordingId}/prepare`, {}).pipe(
      take(1),
      catchError(err => {
        this.loggerService.error(`Error while preparing recording ${recordingId} for notification ${notificationId}`);
        return throwError(() => err);
      })
    );
  }


  /**
   * @description Richiede l'url del video
   */
  public requestVideoUrl$(notificationId: string, recordingId: string): Observable<IVideoRecordingAccessResponse> {
    return this._http.get(`${environment.apiEndpoint}/notifications/${notificationId}/recordings/${recordingId}/access`, {
      responseType: 'text'
    }).pipe(
      take(1),
      map((response) => this.parseAccessResponse(response)),
      catchError(err => {
        this.loggerService.error(`Error while requesting playback URL for recording ${recordingId} and notification ${notificationId}`);
        return throwError(() => err);
      })
    );
  }

  public extractVideoUrl(response: IVideoRecordingAccessResponse): string | null {
    if (this.isNonEmptyString(response)) {
      return response;
    }

    if (!response || typeof response !== 'object') {
      return null;
    }

    const rootLevelUrl = response.url || response.accessUrl || response.playbackUrl || response.videoUrl || response.signedUrl;
    if (this.isNonEmptyString(rootLevelUrl)) {
      return rootLevelUrl;
    }

    const nestedData = response.data;
    if (nestedData && typeof nestedData === 'object') {
      const nestedUrl =
        (nestedData as IVideoRecordingAccessResponseObject).url ||
        (nestedData as IVideoRecordingAccessResponseObject).accessUrl ||
        (nestedData as IVideoRecordingAccessResponseObject).playbackUrl ||
        (nestedData as IVideoRecordingAccessResponseObject).videoUrl ||
        (nestedData as IVideoRecordingAccessResponseObject).signedUrl;

      if (this.isNonEmptyString(nestedUrl)) {
        return nestedUrl;
      }
    }

    return null;
  }

  public isVideoNotReadyError(error: unknown): boolean {
    const httpError = error as Partial<HttpErrorResponse> & {
      error?: { message?: string } | string;
      message?: string;
      status?: number;
    };

    const backendMessage = typeof httpError?.error === 'string'
      ? httpError.error
      : httpError?.error?.message || httpError?.message || '';

    return httpError?.status === 409 || /not ready for playback/i.test(backendMessage);
  }

  private normalizeRecordings(response: unknown): INotificationRecording[] {
    if (Array.isArray(response)) {
      return response.filter((recording): recording is INotificationRecording => this.isNotificationRecording(recording));
    }

    if (response && typeof response === 'object' && Array.isArray((response as { recordings?: unknown[] }).recordings)) {
      return ((response as { recordings?: unknown[] }).recordings || []).filter(
        (recording): recording is INotificationRecording => this.isNotificationRecording(recording)
      );
    }

    return [];
  }

  private isNotificationRecording(recording: unknown): recording is INotificationRecording {
    return !!recording && typeof recording === 'object' && this.isNonEmptyString((recording as INotificationRecording).recordingId);
  }

  private isNonEmptyString(value: unknown): value is string {
    return typeof value === 'string' && value.trim().length > 0;
  }

  private parseAccessResponse(response: string): IVideoRecordingAccessResponse {
    const normalizedResponse = (response || '').trim();

    if (!normalizedResponse) {
      return '';
    }

    try {
      return JSON.parse(normalizedResponse) as IVideoRecordingAccessResponse;
    } catch {
      return normalizedResponse;
    }
  }
}

export interface INotificationRecording {
  recordingId: string;
  status?: string;
  createdAt?: string;
  updatedAt?: string;
  [key: string]: unknown;
}

export interface IVideoRecordingAccessResponseObject {
  url?: string;
  accessUrl?: string;
  playbackUrl?: string;
  videoUrl?: string;
  signedUrl?: string;
  data?: {
    url?: string;
    accessUrl?: string;
    playbackUrl?: string;
    videoUrl?: string;
    signedUrl?: string;
  };
  [key: string]: unknown;
}

export type IVideoRecordingAccessResponse = string | IVideoRecordingAccessResponseObject;
